/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useContext } from 'react';
import { Button, Card, Form, Stack, Table } from 'react-bootstrap';
import { JsonEditor, githubDarkTheme } from 'json-edit-react';
import { useTheme } from 'styled-components';
import {
    fuzzyMatchingGetConfig,
    fuzzyMatchingSetConfig,
    pendingChangesFuzzyPairs,
    pendingChangesUpdateFuzzyPair,
    FuzzyMatchingSettings as FuzzyMatchingSettingsType,
    FuzzyVendorPair,
    FuzzyMatchingToken,
    FuzzyMatchingRule,
} from '../../data';
import { SettingsContext } from '../SettingsContext';
import { StatusContext } from '../StatusContext';

function normalizeNumber(value: string, fallback: number): number {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function tableCellStyle(theme: any) {
    return {
        borderColor: theme?.colors?.textMuted || '#5f6a72',
        color: theme?.colors?.text || '#fff',
        verticalAlign: 'top' as const,
    };
}

function headerCellStyle(theme: any) {
    return {
        ...tableCellStyle(theme),
        backgroundColor: theme?.colors?.headerBg || '#1f2a30',
        color: theme?.colors?.text || '#fff',
    };
}

function FuzzyMatchingSettings() {
    const theme = useTheme() as any;
    const sectionBorder = `1px solid ${theme?.colors?.ctext || '#ccc'}`;
    const settingsContext = useContext(SettingsContext);
    const statusContext = useContext(StatusContext);

    const [settings, setSettings] = useState<FuzzyMatchingSettingsType>({
        min_threshold: 0.85,
        auto_apply_threshold: 0.95,
        verify_threshold: 0.90,
        short_name_max_len: 4,
        short_name_high_similarity: 0.97,
    });
    const [tokens, setTokens] = useState<FuzzyMatchingToken[]>([]);
    const [rules, setRules] = useState<FuzzyMatchingRule[]>([]);
    const [fuzzyPairs, setFuzzyPairs] = useState<FuzzyVendorPair[]>([]);
    const [originalSerialized, setOriginalSerialized] = useState<string>('');
    const [refresh, setRefresh] = useState<number>(0);
    const [pairBusyId, setPairBusyId] = useState<number | null>(null);

    const snapshot = JSON.stringify({ settings, tokens, rules });
    const dirty = snapshot !== originalSerialized;

    useEffect(() => {
        settingsContext.setIsDirty(dirty);
    }, [dirty, settingsContext]);

    useEffect(() => {
        async function load() {
            try {
                const [cfg, fuzzyPairRc] = await Promise.all([
                    fuzzyMatchingGetConfig(),
                    pendingChangesFuzzyPairs(),
                ]);
                setSettings(cfg.settings);
                setTokens(cfg.tokens || []);
                setRules(cfg.rules || []);
                setFuzzyPairs(fuzzyPairRc.items || []);
                setOriginalSerialized(JSON.stringify(cfg));
            } catch (err: any) {
                statusContext.postError(`Failed to load fuzzy matching config: ${err.message}`);
            }
        }
        load();
    }, [refresh]);

    async function save() {
        try {
            const next = await fuzzyMatchingSetConfig({ settings, tokens, rules });
            setSettings(next.settings);
            setTokens(next.tokens || []);
            setRules(next.rules || []);
            setOriginalSerialized(JSON.stringify(next));
            statusContext.postInfo('Fuzzy matching configuration saved');
            setRefresh((r) => r + 1);
        } catch (err: any) {
            statusContext.postError(`Failed to save fuzzy matching config: ${err.message}`);
        }
    }

    async function setFuzzyPairState(pairId: number, state: 'new' | 'verified' | 'rejected', autoApply?: boolean) {
        setPairBusyId(pairId);
        try {
            await pendingChangesUpdateFuzzyPair(pairId, {
                state,
                ...(autoApply === undefined ? {} : { auto_apply: autoApply ? 1 : 0 }),
            } as Partial<FuzzyVendorPair>);
            const fuzzyPairRc = await pendingChangesFuzzyPairs();
            setFuzzyPairs(fuzzyPairRc.items || []);
        } catch (err: any) {
            statusContext.postError(`Failed to update fuzzy pair ${pairId}: ${err.message}`);
        } finally {
            setPairBusyId(null);
        }
    }

    function reset() {
        try {
            const parsed = JSON.parse(originalSerialized || '{}');
            setSettings(parsed.settings || settings);
            setTokens(parsed.tokens || []);
            setRules(parsed.rules || []);
        } catch {
            // keep current values when reset snapshot is invalid
        }
    }

    const changedStyle = (changed: boolean): React.CSSProperties => changed ? {
        borderLeft: `3px solid ${theme?.colors?.info || '#0dcaf0'}`,
        paddingLeft: '0.75rem'
    } : {};

    return (
        <div>
            <div className="d-flex justify-content-between align-items-start mb-4" style={{ borderBottom: sectionBorder, paddingBottom: '0.75rem' }}>
                <div>
                    <h3 className="mb-1">Fuzzy Matching</h3>
                    <div className="text-muted">Vendor fuzzy canonicalization policy, thresholds, token classes, and deterministic rule actions.</div>
                </div>
                {dirty && (
                    <Stack direction="horizontal" gap={2}>
                        <Button variant="outline-secondary" onClick={reset}>Clear</Button>
                        <Button variant="primary" onClick={save}>Save</Button>
                    </Stack>
                )}
            </div>

            <div className="mb-4" style={changedStyle(dirty)}>
                <Form.Label className="fw-semibold">Thresholds</Form.Label>
                <div className="text-muted small mb-2">These values are stored under settings and applied by cleanup fuzzy vendor planning.</div>
                <div className="d-flex flex-wrap" style={{ gap: '0.75rem', maxWidth: '64rem' }}>
                    <Form.Group>
                        <Form.Label htmlFor="fuzzy-min-threshold" className="small">Min Similarity</Form.Label>
                        <Form.Control
                            id="fuzzy-min-threshold"
                            type="number"
                            step="0.001"
                            value={settings.min_threshold}
                            onChange={(e) => setSettings((s) => ({ ...s, min_threshold: normalizeNumber(e.target.value, s.min_threshold) }))}
                        />
                        <div className="text-muted small mt-1">Ignore candidate pairs below this similarity score.</div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label htmlFor="fuzzy-auto-threshold" className="small">Auto Apply</Form.Label>
                        <Form.Control
                            id="fuzzy-auto-threshold"
                            type="number"
                            step="0.001"
                            value={settings.auto_apply_threshold}
                            onChange={(e) => setSettings((s) => ({ ...s, auto_apply_threshold: normalizeNumber(e.target.value, s.auto_apply_threshold) }))}
                        />
                        <div className="text-muted small mt-1">Pairs at or above this score may be auto-merged by enabled rules.</div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label htmlFor="fuzzy-verify-threshold" className="small">Verify</Form.Label>
                        <Form.Control
                            id="fuzzy-verify-threshold"
                            type="number"
                            step="0.001"
                            value={settings.verify_threshold}
                            onChange={(e) => setSettings((s) => ({ ...s, verify_threshold: normalizeNumber(e.target.value, s.verify_threshold) }))}
                        />
                        <div className="text-muted small mt-1">Scores above this are high-confidence review candidates when not auto-applied.</div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label htmlFor="fuzzy-short-max" className="small">Short Name Max Len</Form.Label>
                        <Form.Control
                            id="fuzzy-short-max"
                            type="number"
                            step="1"
                            value={settings.short_name_max_len}
                            onChange={(e) => setSettings((s) => ({ ...s, short_name_max_len: normalizeNumber(e.target.value, s.short_name_max_len) }))}
                        />
                        <div className="text-muted small mt-1">Vendor roots with length at or below this are treated as short/generic names.</div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label htmlFor="fuzzy-short-sim" className="small">Short Name Strict Similarity</Form.Label>
                        <Form.Control
                            id="fuzzy-short-sim"
                            type="number"
                            step="0.001"
                            value={settings.short_name_high_similarity}
                            onChange={(e) => setSettings((s) => ({ ...s, short_name_high_similarity: normalizeNumber(e.target.value, s.short_name_high_similarity) }))}
                        />
                        <div className="text-muted small mt-1">Short/generic names must meet at least this similarity to avoid rejection rules.</div>
                    </Form.Group>
                </div>
            </div>

            <div className="mb-4">
                <Form.Label className="fw-semibold">Token Classes</Form.Label>
                <div className="text-muted small mb-2">
                    Token classes define how vendor words are interpreted by fuzzy matching rules. Decoration and minor qualifier tokens are usually safe suffixes
                    like "ai" or "app", generic brand tokens identify weak roots such as "chat" or "bot", and category-drift / overloaded-root tokens help
                    prevent false merges across unrelated products. Edit these rows to tune how aggressively names are grouped.
                </div>
                <JsonEditor
                    data={tokens}
                    collapse={1}
                    rootName="fuzzy_match_token"
                    rootFontSize="13px"
                    theme={githubDarkTheme}
                    onUpdate={({ newData }) => setTokens(Array.isArray(newData) ? newData as FuzzyMatchingToken[] : tokens)}
                />
            </div>

            <div className="mb-4">
                <Form.Label className="fw-semibold">Rule Actions</Form.Label>
                <div className="text-muted small mb-2">
                    Rule actions map each matching heuristic to an outcome: auto-merge, manual review, or reject. Priority controls evaluation order, and enabled
                    flags let you stage or disable specific logic without deleting it. This table is the policy layer that decides what happens after similarity and
                    token analysis are computed.
                </div>
                <JsonEditor
                    data={rules}
                    collapse={1}
                    rootName="fuzzy_match_rule"
                    rootFontSize="13px"
                    theme={githubDarkTheme}
                    onUpdate={({ newData }) => setRules(Array.isArray(newData) ? newData as FuzzyMatchingRule[] : rules)}
                />
            </div>

            <Card className="mb-4">
                <Card.Header as="h5">Fuzzy Vendor Pair Table</Card.Header>
                <Card.Body>
                    <div className="text-muted small mb-3">
                        This table stores observed vendor-name pairs produced by the canonicalization pipeline, along with similarity, workflow state, and optional
                        canonical target. Use it to curate difficult matches, mark trusted pairs for auto-apply, and reject noisy pairs so they do not keep showing
                        up in cleanup planning.
                    </div>
                    <div style={{ maxHeight: '32vh', overflowY: 'auto' }}>
                        <Table bordered hover size="sm" style={{ tableLayout: 'fixed', width: '100%' }}>
                            <thead>
                                <tr>
                                    <th style={{ ...headerCellStyle(theme), width: 56 }}>ID</th>
                                    <th style={{ ...headerCellStyle(theme), width: 220 }}>Pair</th>
                                    <th style={{ ...headerCellStyle(theme), width: 90 }}>Similarity</th>
                                    <th style={{ ...headerCellStyle(theme), width: 90 }}>State</th>
                                    <th style={{ ...headerCellStyle(theme), width: 70 }}>Auto</th>
                                    <th style={{ ...headerCellStyle(theme), width: 220 }}>Canonical</th>
                                    <th style={{ ...headerCellStyle(theme), width: 70 }}>Seen</th>
                                    <th style={{ ...headerCellStyle(theme), width: 240 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fuzzyPairs.map((pair) => (
                                    <tr key={pair.id}>
                                        <td style={tableCellStyle(theme)}>{pair.id}</td>
                                        <td style={{ ...tableCellStyle(theme), whiteSpace: 'normal', wordBreak: 'break-word' }}>{pair.vendor_a_norm} / {pair.vendor_b_norm}</td>
                                        <td style={tableCellStyle(theme)}>{Number(pair.similarity || 0).toFixed(3)}</td>
                                        <td style={tableCellStyle(theme)}>{pair.state}</td>
                                        <td style={tableCellStyle(theme)}>{Number(pair.auto_apply || 0) === 1 ? 'yes' : 'no'}</td>
                                        <td style={{ ...tableCellStyle(theme), whiteSpace: 'normal', wordBreak: 'break-word' }}>{pair.canonical_vendor || ''} {pair.canonical_vendor_tag ? `(${pair.canonical_vendor_tag})` : ''}</td>
                                        <td style={tableCellStyle(theme)}>{pair.seen_count}</td>
                                        <td style={tableCellStyle(theme)}>
                                            <div className="d-flex gap-2 flex-wrap">
                                                <Button size="sm" variant="outline-success" disabled={pairBusyId !== null} onClick={() => setFuzzyPairState(pair.id, 'verified', true)}>Verify+Auto</Button>
                                                <Button size="sm" variant="outline-secondary" disabled={pairBusyId !== null} onClick={() => setFuzzyPairState(pair.id, 'new')}>Reset</Button>
                                                <Button size="sm" variant="outline-danger" disabled={pairBusyId !== null} onClick={() => setFuzzyPairState(pair.id, 'rejected', false)}>Reject</Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {fuzzyPairs.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="text-center text-muted" style={tableCellStyle(theme)}>No fuzzy vendor pairs loaded.</td>
                                    </tr>
                                ) : null}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
}

export default FuzzyMatchingSettings;
